import type { JSX } from "react";
import type { DeviceClassification, DeviceProfile } from "../types";
import {
  labelForProperty,
  labelForSource,
  type ClassificationProperty,
} from "./labels";
import { ValueWithRangePanel } from "./ValueWithRangePanel";

type ScoredScalar = {
  value: string;
  certainty: number;
  source: "lookalike" | "ai" | "manual" | "fact";
};

const SCALAR_LABELS: { key: keyof DeviceClassification; label: string }[] = [
  { key: "gender", label: "Gender" },
  { key: "ageGroup", label: "Age group" },
  { key: "incomeTier", label: "Income tier" },
  { key: "familyStatus", label: "Family status" },
  { key: "maritalStatus", label: "Marital status" },
  { key: "mediaConsumptionType", label: "Media consumption" },
];

const SOURCE_COLORS: Record<ScoredScalar["source"], string> = {
  ai: "bg-(--pastel-blue) text-(--status-info)",
  manual: "bg-(--pastel-green) text-(--status-success)",
  fact: "bg-(--pastel-violet) text-(--brand-primary)",
  lookalike: "bg-(--pastel-amber) text-(--status-warn)",
};

const certaintyBarColor = (c: number): string => {
  if (c >= 0.8) return "bg-emerald-500";
  if (c >= 0.6) return "bg-blue-500";
  if (c >= 0.4) return "bg-amber-500";
  return "bg-rose-500";
};

const SourceBadge = ({
  source,
}: {
  source: ScoredScalar["source"];
}): JSX.Element => (
  <span
    className={`inline-block rounded-(--radius-sm) px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${SOURCE_COLORS[source]}`}
  >
    {labelForSource(source)}
  </span>
);

const CertaintyBar = ({ value }: { value: number }): JSX.Element => (
  <div className="flex items-center gap-2">
    <div className="h-1.5 w-24 overflow-hidden rounded-(--radius-pill) bg-(--surface-2)">
      <div
        className={`h-full ${certaintyBarColor(value)}`}
        style={{ width: `${Math.round(value * 100)}%` }}
      />
    </div>
    <span className="text-xs text-(--text-secondary)">
      {Math.round(value * 100)}%
    </span>
  </div>
);

const ScalarRow = ({
  label,
  property,
  entry,
}: {
  label: string;
  property: ClassificationProperty;
  entry: ScoredScalar;
}): JSX.Element => (
  <div className="grid grid-cols-[minmax(140px,1fr)_minmax(160px,1.4fr)_minmax(140px,1fr)_auto] items-center gap-3 border-b border-(--border-subtle) py-2 text-sm last:border-0">
    <span className="text-(--text-secondary)">{label}</span>
    <span className="rounded-(--radius-md) bg-(--surface-1) px-2 py-0.5 font-semibold text-(--text-primary)">
      {labelForProperty(property, entry.value)}
    </span>
    <CertaintyBar value={entry.certainty} />
    <SourceBadge source={entry.source} />
  </div>
);

const ChipList = ({
  title,
  property,
  entries,
}: {
  title: string;
  property: ClassificationProperty;
  entries: ScoredScalar[];
}): JSX.Element | null => {
  if (entries.length === 0) return null;
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold uppercase text-(--text-secondary)">
        {title}
      </h4>
      <ul className="flex flex-col gap-2">
        {[...entries]
          .sort((a, b) => b.certainty - a.certainty)
          .map((e) => (
            <li
              key={`${e.value}-${e.source}`}
              className="grid grid-cols-[minmax(140px,1fr)_minmax(140px,1fr)_auto] items-center gap-3 rounded-(--radius-md) bg-(--surface-2) px-3 py-1.5"
            >
              <span className="text-sm font-semibold text-(--text-primary)">
                {labelForProperty(property, e.value)}
              </span>
              <CertaintyBar value={e.certainty} />
              <SourceBadge source={e.source} />
            </li>
          ))}
      </ul>
    </div>
  );
};

export const hasClassificationContent = (profile: DeviceProfile): boolean => {
  const c = profile.classification;
  const scalarHas = SCALAR_LABELS.some(({ key }) => c[key] != null);
  const chipHas =
    c.interests.length > 0 ||
    c.kidsDemographics.length > 0 ||
    c.mandatoryIntent.length > 0;
  const historyHas =
    profile.demographics.gender.length > 0 ||
    profile.demographics.ageGroup.length > 0;
  return scalarHas || chipHas || historyHas;
};

export const ClassificationPanel = ({
  profile,
}: {
  profile: DeviceProfile;
}): JSX.Element | null => {
  const c = profile.classification;
  const presentScalars = SCALAR_LABELS.filter(({ key }) => c[key] != null);
  const allChipLists: {
    title: string;
    property: ClassificationProperty;
    entries: ScoredScalar[];
  }[] = [
    { title: "Interests", property: "interest", entries: c.interests },
    {
      title: "Kids demographics",
      property: "kidsDemographics",
      entries: c.kidsDemographics,
    },
    {
      title: "Mandatory intent",
      property: "mandatoryIntent",
      entries: c.mandatoryIntent,
    },
  ];
  const chipLists = allChipLists.filter((cl) => cl.entries.length > 0);

  const showInferred = presentScalars.length > 0 || chipLists.length > 0;
  const showGenderHistory = profile.demographics.gender.length > 0;
  const showAgeHistory = profile.demographics.ageGroup.length > 0;
  const showHistory = showGenderHistory || showAgeHistory;

  if (!showInferred && !showHistory) return null;

  return (
    <div className="space-y-6">
      {showInferred ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-(--text-secondary)">
            Inferred classification
          </h3>
          {presentScalars.length > 0 ? (
            <div className="rounded-(--radius-lg) bg-(--surface-2) px-4 py-2">
              {presentScalars.map(({ key, label }) => (
                <ScalarRow
                  key={key}
                  label={label}
                  property={key as ClassificationProperty}
                  entry={c[key] as ScoredScalar}
                />
              ))}
            </div>
          ) : null}
          {chipLists.length > 0 ? (
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {chipLists.map((cl) => (
                <ChipList
                  key={cl.title}
                  title={cl.title}
                  property={cl.property}
                  entries={cl.entries}
                />
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {showHistory ? (
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-(--text-secondary)">
            Per-impression tags (observed in VAST)
          </h3>
          <div className="space-y-4">
            {showGenderHistory ? (
              <div>
                <h4 className="mb-2 text-sm font-semibold uppercase text-(--text-secondary)">
                  Gender history
                </h4>
                <ValueWithRangePanel
                  rows={profile.demographics.gender}
                  valueLabel="Gender"
                  property="gender"
                />
              </div>
            ) : null}
            {showAgeHistory ? (
              <div>
                <h4 className="mb-2 text-sm font-semibold uppercase text-(--text-secondary)">
                  Age-group history
                </h4>
                <ValueWithRangePanel
                  rows={profile.demographics.ageGroup}
                  valueLabel="Age group"
                  property="ageGroup"
                />
              </div>
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
};
